import { FC, useMemo, useState } from 'react';

import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { AxiosResponse } from 'axios';
import { endOfToday, isAfter, isValid, startOfToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { GetBaseQueueOptions, BaseQueueItems } from 'components/types';

import {
  formatByLocale,
  fromISOString,
  getMinDate,
  toLocalISOString
} from 'utils/date';

import { Box } from '@mui/material';

import { DatePicker, FontAwesomeSvgIcon, Tooltip } from 'components/shared';
import { DataGrid } from 'components/shared/DataGrid';
import {
  DataGridAction,
  DataGridActionsFn
} from 'components/shared/DataGrid/DataGrid';

export type BaseQueueProperties = {
  queueName: string;
  columns: string[];
  getBaseQueue: ({
    params,
    signal
  }: GetBaseQueueOptions) => Promise<AxiosResponse<BaseQueueItems, any>>;
  rowMapper?: (row: any) => any;
  customActions?: DataGridAction<any>[] | DataGridActionsFn<any>;
};

const BaseQueue: FC<BaseQueueProperties> = ({
  queueName,
  columns,
  getBaseQueue,
  rowMapper,
  customActions
}) => {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date | null>(startOfToday());
  const [endDate, setEndDate] = useState<Date | null>(endOfToday());
  const [pageSize, setPageSize] = useState<number>(10);

  const baseColumns = columns.map((key) => ({
    field: key,
    headerName: t(`adminPortal.${queueName}.${key}`),
    flex: 1
  }));

  const isDateInputValid = useMemo(() => {
    if (!(isValid(startDate) && isValid(endDate))) {
      return false;
    }

    if (startDate && endDate) {
      return !isAfter(startDate, endDate);
    }

    return false;
  }, [startDate, endDate]);

  const { data: baseQueue = [], isLoading } = useQuery(
    ['BaseQueue', startDate, endDate],
    async ({ signal }) => {
      const { data } = await getBaseQueue({
        params: {
          start: toLocalISOString(startDate as Date),
          end: toLocalISOString(endDate as Date)
        },
        signal
      });
      return data;
    },
    {
      enabled: isDateInputValid && isAfter(startDate as Date, getMinDate())
    }
  );

  const rows = baseQueue
    .map((o) => (rowMapper ? rowMapper(o) : o))
    .map((o) => ({
      ...o,
      started: formatByLocale(fromISOString(o.started), 'Pp'),
      inserted: formatByLocale(fromISOString(o.inserted), 'Pp'),
      ended: formatByLocale(fromISOString(o.ended), 'Pp')
    }));

  return (
    <Box>
      <Box display="flex" gap={2} mt={3} alignItems="flex-end">
        <DatePicker
          showInput
          label={t('startDate')}
          onChange={(date) => {
            if (date) {
              setStartDate(date);
            }
          }}
          date={startDate}
        />
        <DatePicker
          showInput
          label={t('endDate')}
          onChange={(date) => {
            if (date) {
              setEndDate(date);
            }
          }}
          date={endDate}
        />
        {!isDateInputValid && (
          <Tooltip
            title={
              t('dateRangeMismatch', {
                startDate: t('startDate'),
                endDate: t('endDate')
              }) || ''
            }
          >
            <FontAwesomeSvgIcon color="warning" icon={faExclamationCircle} />
          </Tooltip>
        )}
      </Box>
      <Box>
        <DataGrid
          rows={rows}
          columns={baseColumns}
          loading={isLoading}
          initialState={{
            sorting: {
              sortModel: [{ field: 'id', sort: 'desc' }]
            }
          }}
          pagination
          pageSize={pageSize}
          rowsPerPageOptions={[10, 20, 50]}
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          customActions={customActions}
        />
      </Box>
    </Box>
  );
};

export default BaseQueue;
